import React, { useCallback, useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { Paper, TextField } from '@mui/material';
// import _ from 'lodash';

import { auth } from 'src/app/database';

import { sendPasswordResetEmail } from 'src/store/app';
import {
  getIssuerDataSelector,
  updateProfileData,
  getIsEditProfileSelector,
  setIsEditProfile,
  isLoadingIssuerDataSelector,
} from 'src/store/issuer';

import { ERROR } from 'src/constants/errorNames';
import { phoneE164Regex } from 'src/constants/regularExpression';
import { formatPhoneNumber } from 'src/utils/format';

import { UploadImage } from 'src/features/uploadImage/UploadImage';
import {
  Button,
  Status,
  InputPhone,
  // CoverageLines,
  LoaderWrapper,
  LogoLoader,
} from 'src/components';

import classes from './issuerProfile.module.scss';
import { isLoadingUpdateProfileSelector } from 'src/store/broker';

export const IssuerProfile = () => {
  const dispatch = useDispatch();

  const [user] = useAuthState(auth);
  const issuerData = useSelector(getIssuerDataSelector);
  const isLoadingIssuerData = useSelector(isLoadingIssuerDataSelector);
  const isLoadingUpdateProfile = useSelector(isLoadingUpdateProfileSelector);
  const isEditMode = useSelector(getIsEditProfileSelector);

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('activeTab');

  const [fullName, setFullName] = useState(user?.displayName);
  const [email, setEmail] = useState(user?.email);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [previewImage, setPreviewImage] = useState(issuerData?.photo_url);
  // const [selectedLines, setSelectedLines] = useState([]);

  useEffect(() => {
    dispatch(setIsEditProfile(false));
  }, [dispatch]);

  // useEffect(() => {
  //   if (!_.isEmpty(issuerData)) {
  //     if (_.isEmpty(issuerData?.lines)) {
  //       dispatch(setIsEditProfile(true));
  //     }
  //   }
  // }, [dispatch, issuerData]);

  useEffect(() => {
    setPhoneNumber(issuerData?.phone_number ?? '');
  }, [isEditMode, issuerData]);

  useEffect(() => {
    if (issuerData?.photo_url) {
      setPreviewImage(issuerData?.photo_url);
    }
  }, [issuerData]);

  const onSetPreviewImage = useCallback((image) => {
    setPreviewImage(image);
  }, []);

  const onChangeFullName = useCallback((event) => {
    setFullName(event.target.value);
  }, []);

  const onChangeEmail = useCallback((event) => {
    setEmail(event.target.value);
  }, []);

  const onChangePhoneNumber = useCallback((phone) => {
    setPhoneNumber(phone);
  }, []);

  const onClickEditProfile = useCallback(() => {
    dispatch(setIsEditProfile((prev) => !prev));
  }, [dispatch]);

  const onSendResetPassword = useCallback(() => {
    dispatch(sendPasswordResetEmail({ email, replacedUrl: '/issuer/settings', isIssuer: true }));
  }, [dispatch, email]);

  const onSaveUpdatedUserProfile = useCallback(() => {
    if (phoneNumber.includes('+') && !phoneNumber.match(phoneE164Regex)) {
      return toast(ERROR.INVALID_PHONE_NUMBER, { type: 'warning' });
    }

    if (!phoneNumber.includes('+') && !formatPhoneNumber(phoneNumber).match(phoneE164Regex)) {
      return toast(ERROR.INVALID_PHONE_NUMBER, { type: 'warning' });
    }

    const payload = {
      email,
      fullName,
      photo_url: previewImage,
      phone_number: !phoneNumber.includes('+') ? formatPhoneNumber(phoneNumber) : phoneNumber,
      // lines: selectedLines,
    };

    dispatch(updateProfileData(payload));
  }, [dispatch, email, fullName, phoneNumber, previewImage]);

  const onCancelEditProfile = useCallback(() => {
    dispatch(setIsEditProfile(false));
    setFullName(user?.displayName);
    setEmail(user?.email);
    setPreviewImage(issuerData?.photo_url || '');
  }, [dispatch, user, issuerData]);

  // const onChangeSelectedLines = useCallback((payload) => {
  //   setSelectedLines(payload);
  // }, []);

  return (
    <>
      <div className={classes.IssuerProfile}>
        <Paper sx={{ padding: 2 }}>
          {isLoadingIssuerData || isLoadingUpdateProfile ? (
            <LoaderWrapper>
              <LogoLoader />
            </LoaderWrapper>
          ) : (
            <>
              <div className={classes.IssuerProfileTitle}>{activeTab}</div>
              <div className={classes.IssuerProfileContent}>
                <UploadImage
                  path={`users/${user?.uid}/avatar`}
                  previewImage={previewImage}
                  setPreviewImage={onSetPreviewImage}
                  isOnlyImage={!isEditMode}
                  type="profile"
                  isCirclePreviewImage
                />

                <div className={classes.IssuerProfileGrid}>
                  <TextField
                    size="small"
                    label="Full Name"
                    value={fullName}
                    onChange={onChangeFullName}
                    disabled={!isEditMode}
                  />
                  <TextField
                    size="small"
                    label="Email"
                    value={email}
                    type="email"
                    onChange={onChangeEmail}
                    disabled={!isEditMode}
                  />
                  <InputPhone
                    label="Phone Number"
                    value={phoneNumber}
                    onChange={onChangePhoneNumber}
                    disabled={!isEditMode}
                  />

                  <TextField
                    size="small"
                    label="Email Verified"
                    value={user?.emailVerified.toString()}
                    disabled
                  />
                  {!isEditMode ? (
                    <TextField size="small" label="Password" value="********" disabled />
                  ) : (
                    <Button title="Change Password" type="primary" onClick={onSendResetPassword} />
                  )}
                  <div className={classes.IssuerProfileStatus}>
                    Subscription Status:
                    <Status status={issuerData?.status} />
                  </div>
                </div>

                {/* <div className={classes.IssuerProfileCoverageLines}>
                  <h4>Lines Supported</h4>
                  <CoverageLines
                    data={selectedLines}
                    onSelect={onChangeSelectedLines}
                    onDeselect={onChangeSelectedLines}
                    disabled={!isEditMode}
                  />
                </div> */}

                <div className={classes.IssuerProfileFooterButton}>
                  {!isEditMode && (
                    <Button title="Edit profile" type="secondary" onClick={onClickEditProfile} />
                  )}

                  {isEditMode && (
                    <>
                      <Button title="Save" type="primary" onClick={onSaveUpdatedUserProfile} />
                      <Button title="Cancel" type="secondary" onClick={onCancelEditProfile} />
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </Paper>
      </div>
    </>
  );
};
